import React, { useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { NumericTextBox, Input, Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import OwnerAutoComplete, { OwnerAutoCompleteViewModel } from '../../AssetOwners/OwnerAutoComplete';
import VehicleAutoComplete from '../../AssetVehicles/VehicleAutoComplete';
import PartAutoComplete from '../PartAutoComplete';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from 'TypeGen/json-response';
import { formatCurrency } from './WorkOrderDetail';

type Props = {
  onClose: () => void;
  onCreated: (workOrderId?: number) => void;
};

type SaleLine = {
  key: number;
  isCustom: boolean;
  partId: number | null;
  partNumber: string;
  customDescription: string;
  quantity: number;
  unitPrice: number;
};

let lineKeySeed = 1;

const newLine = (): SaleLine => ({
  key: lineKeySeed++,
  isCustom: true,
  partId: null,
  partNumber: '',
  customDescription: '',
  quantity: 1,
  unitPrice: 0
});

const CreateCounterSaleDialog: React.FC<Props> = ({ onClose, onCreated }) => {
  const [ownerId, setOwnerId] = useState<number | null>(null);
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [lines, setLines] = useState<SaleLine[]>([newLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0),
    [lines]
  );

  const updateLine = (key: number, changes: Partial<SaleLine>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...changes } : line)));
  };

  const removeLine = (key: number) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((line) => line.key !== key) : prev));
  };

  const isLineValid = (line: SaleLine) =>
    line.quantity > 0 &&
    line.unitPrice >= 0 &&
    (line.isCustom ? line.customDescription.trim().length > 0 : !!line.partId);

  const canSave = useMemo(
    () => !saving && !!ownerId && lines.length > 0 && lines.every(isLineValid),
    [saving, ownerId, lines]
  );

  const handleSave = async () => {
    if (!canSave || !ownerId) {
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      OwnerID: ownerId,
      VehicleID: vehicleId,
      Lines: lines.map((line) => ({
        PartID: line.isCustom ? null : line.partId,
        PartLocationID: null as number | null,
        PurchaseOrderNumber: null as number | null,
        QuantityUsed: line.quantity,
        CostAtTime: line.unitPrice,
        Note: null as string | null,
        IsCustomPart: line.isCustom,
        CustomPartDescription: line.isCustom ? line.customDescription.trim() : null
      }))
    };

    try {
      const response = await fetchApi('/api/ShopWorkOrder/CreateCounterSale', payload, 'POST') as JsonResponse;
      if (!response.Success) {
        setError(response.ErrorMessage || 'Unable to create counter sale.');
        setSaving(false);
        return;
      }

      const createdLink = response.Links.find((link) => link?.Name === 'WorkOrder');
      const segments = createdLink?.Link.split('/') ?? [];
      onCreated(segments.length ? Number(segments[segments.length - 1]) : undefined);
    } catch (err: any) {
      console.error('Failed to create counter sale.', err);
      setError(err?.Detail || err?.message || 'Unable to create counter sale.');
      setSaving(false);
    }
  };

  return (
    <Dialog title="New Counter Sale" onClose={onClose} className="dialog-w1140">
      <div className="k-form k-form-md">
        {saving && (
          <div className="mb-3 d-flex justify-content-center">
            <Loader type="converging-spinner" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label fw-bold">Customer (Owner)</label>
          <OwnerAutoComplete
            style={{ width: '100%' }}
            placeholder="Select Owner"
            onSelectedOwner={(owner) => setOwnerId((owner as OwnerAutoCompleteViewModel | null)?.OwnerID ?? null)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Vehicle (Optional)</label>
          <VehicleAutoComplete
            selectedVehicleID={vehicleId ?? undefined}
            onSelectedVehicle={(vehicle) => setVehicleId(vehicle?.VehicleID ?? null)}
            placeholder="Select Unit"
            disabled={saving}
            style={{ width: '100%' }}
          />
        </div>

        <label className="form-label fw-bold">Items</label>
        <div className="table-responsive">
          <table className="table table-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col />
              <col style={{ width: 130 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 50 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Item</th>
                <th className="text-end">Qty</th>
                <th className="text-end">Unit Price</th>
                <th className="text-end">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.key}>
                  <td>
                    <div className="input-group input-group-sm">
                      <div className="input-group-prepend">
                        <span className="input-group-text">
                          <Checkbox
                            value={line.isCustom}
                            disabled={saving}
                            onChange={(e: CheckboxChangeEvent) =>
                              updateLine(line.key, e.value
                                ? { isCustom: true, partId: null, partNumber: '' }
                                : { isCustom: false, customDescription: '' })
                            }
                          />
                          <span className="ml-1">Custom</span>
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        {line.isCustom ? (
                          <Input
                            placeholder="Item sold description..."
                            value={line.customDescription}
                            maxLength={200}
                            disabled={saving}
                            onChange={(e) => updateLine(line.key, { customDescription: e.value as string })}
                            style={{ width: '100%' }}
                          />
                        ) : (
                          <PartAutoComplete
                            onlyActiveParts
                            disabled={saving}
                            style={{ width: '100%' }}
                            onSelectedPart={(part) =>
                              updateLine(line.key, {
                                partId: part?.PartID ?? null,
                                partNumber: part?.PartNumber ?? '',
                                // Prefill the price from cost as a starting point; the user can override.
                                unitPrice: part && line.unitPrice === 0 ? part.AverageCost : line.unitPrice
                              })
                            }
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-end">
                    <NumericTextBox
                      value={line.quantity}
                      min={line.isCustom ? 0.001 : 1}
                      step={1}
                      format={line.isCustom ? 'n3' : 'n0'}
                      disabled={saving}
                      onChange={(e) => updateLine(line.key, { quantity: e.value ?? 0 })}
                      width="100%"
                    />
                  </td>
                  <td className="text-end">
                    <NumericTextBox
                      value={line.unitPrice}
                      min={0}
                      format="c2"
                      disabled={saving}
                      onChange={(e) => updateLine(line.key, { unitPrice: e.value ?? 0 })}
                      width="100%"
                    />
                  </td>
                  <td className="text-end align-middle">{formatCurrency(line.quantity * line.unitPrice)}</td>
                  <td className="text-end align-middle">
                    <Button
                      type="button"
                      fillMode="flat"
                      svgIcon={trashIcon}
                      title="Remove item"
                      disabled={saving || lines.length === 1}
                      onClick={() => removeLine(line.key)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="text-end fw-bold">Total</td>
                <td className="text-end fw-bold">{formatCurrency(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        <Button type="button" fillMode="flat" themeColor="primary" svgIcon={plusIcon} disabled={saving} onClick={() => setLines((prev) => [...prev, newLine()])}>
          Add Item
        </Button>
      </div>
      <DialogActionsBar>
        <Button type="button" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" themeColor="primary" disabled={!canSave} onClick={handleSave}>
          {saving ? 'Creating...' : 'Create Sale'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default CreateCounterSaleDialog;
